import { useMemo } from "react";
import { LightningBoltIcon, PurchaseBagIcon, TrophyIcon } from "../icons";
import { usePublicBlocksExperience } from "../blocks/usePublicBlocksExperience";

export type LotteryNavActiveTab = "lottery" | "leaderboard" | "account";

function formatSats(value: number | null): string {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  return Math.max(0, Math.trunc(value)).toLocaleString("fa-IR");
}

export function LotteryBottomNav({ activeTab }: { activeTab: LotteryNavActiveTab }) {
  const experience = usePublicBlocksExperience();
  const isLoggedIn = experience.snapshot?.viewer?.isLoggedIn === true;
  const withdrawableSats = useMemo(() => {
    if (!isLoggedIn) return null;
    const raw = experience.snapshot?.balance?.withdrawableSats ?? 0;
    return Number.isFinite(raw) ? raw : null;
  }, [experience.snapshot?.balance?.withdrawableSats, isLoggedIn]);

  const commonItemClass =
    "group relative flex flex-col items-center justify-center gap-1 overflow-hidden rounded-xl border px-3 py-2 text-xs font-semibold transition shadow-[0_8px_30px_rgba(8,15,40,0.35)]";
  const commonAccentClass =
    "pointer-events-none absolute inset-x-2 -top-3 h-3 rounded-t-full transition duration-200";

  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-800/70 bg-slate-950/80 backdrop-blur-xl shadow-[0_-16px_48px_rgba(8,15,40,0.55)]">
      <div className="mx-auto max-w-6xl px-4 pb-[env(safe-area-inset-bottom,0px)] pt-3">
        <div className="grid grid-cols-3 gap-3">
          <a
            href="/#account"
            className={`${commonItemClass} col-span-1 ${
              activeTab === "account"
                ? "border-amber-300/80 bg-amber-500/10 text-amber-50"
                : "border-amber-300/40 bg-slate-900/70 text-amber-100/90 hover:border-amber-300/60"
            }`}
            aria-current={activeTab === "account" ? "page" : undefined}
            aria-label="نمایش حساب کاربری"
          >
            <span
              aria-hidden
              className={`${commonAccentClass} ${
                activeTab === "account"
                  ? "bg-gradient-to-t from-amber-400/35 via-amber-300/10 to-transparent"
                  : "bg-gradient-to-t from-amber-300/10 via-transparent to-transparent"
              }`}
            />
            <div className="inline-flex items-center gap-0.5 text-sm font-semibold leading-none tracking-tight">
              <span className="tabular-nums leading-none" dir="ltr">{formatSats(withdrawableSats)}</span>
              <LightningBoltIcon className="h-4 w-4 text-amber-300 -ms-0.5" aria-hidden />
            </div>
            <span className="text-[11px] text-amber-100/90">حساب کاربری</span>
          </a>
          <a
            href="/#lottery-bitcoin"
            className={`${commonItemClass} ${
              activeTab === "lottery"
                ? "border-sky-300/80 bg-sky-500/10 text-sky-50"
                : "border-sky-300/40 bg-slate-900/70 text-sky-100/90 hover:border-sky-300/60"
            }`}
            aria-current={activeTab === "lottery" ? "page" : undefined}
            aria-label="بازگشت به صفحه قرعه‌کشی بیت‌کوین"
          >
            <span
              aria-hidden
              className={`${commonAccentClass} ${
                activeTab === "lottery"
                  ? "bg-gradient-to-t from-sky-400/40 via-sky-300/15 to-transparent"
                  : "bg-gradient-to-t from-sky-300/10 via-transparent to-transparent"
              }`}
            />
            <PurchaseBagIcon className="h-5 w-5" aria-hidden />
            <span className="text-[11px]">قرعه‌کشی</span>
          </a>
          <a
            href="/#lottery-leaderboard"
            className={`${commonItemClass} ${
              activeTab === "leaderboard"
                ? "border-emerald-300/80 bg-emerald-500/10 text-emerald-50"
                : "border-emerald-300/40 bg-slate-900/70 text-emerald-100/90 hover:border-emerald-300/60"
            }`}
            aria-current={activeTab === "leaderboard" ? "page" : undefined}
            aria-label="جدول برترین‌های لاتاری"
          >
            <span
              aria-hidden
              className={`${commonAccentClass} ${
                activeTab === "leaderboard"
                  ? "bg-gradient-to-t from-emerald-400/35 via-emerald-300/10 to-transparent"
                  : "bg-gradient-to-t from-emerald-300/10 via-transparent to-transparent"
              }`}
            />
            <TrophyIcon className="h-5 w-5" aria-hidden />
            <span className="text-[11px]">برترین‌ها</span>
          </a>
        </div>
      </div>
    </nav>
  );
}
